import { createElement, type ReactNode, useCallback, useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { ConvexProviderWithAuth, ConvexReactClient, useMutation, useQuery } from 'convex/react';

import { selectUser } from 'store';

import { useAppSelector } from 'hooks';
import type { CallTopic } from 'services/calls/calls.types';

import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';

import type {
  AiAssistantImageAttachment,
  AiAssistantMessage,
  AiAssistantMessageStatus,
  AiAssistantThreadDetail,
  AiAssistantThreadSummary,
  CreateAiAssistantMessageRequest,
  CreateAiAssistantMessageResult,
  GetAiAssistantThreadResponse,
  GetAiAssistantThreadsResponse
} from './aiAssistantChat.types';

const AI_ASSISTANT_THREAD_KIND = 'patient_portal' as const;
const AI_ASSISTANT_WORKFLOW_ID = AI_ASSISTANT_THREAD_KIND;
const CONVEX_AUTH_REFRESH_LEEWAY_MS = 30_000;
const DEFAULT_AI_THREAD_TITLE = 'New Message';

type ConvexThreadSummary = {
  conversationTitle: string;
  createdAt: number;
  id: string;
  latestPreview: string;
  status: 'complete' | 'in_progress';
  updatedAt: number;
};

type ConvexThreadAccess = {
  completedAt: number | null;
  conversationTitle: string;
  createdAt: number | null;
  isClaimed: boolean;
  status: 'complete' | 'in_progress' | null;
  threadId: string;
  updatedAt: number | null;
};

type ConvexPromptRecord = {
  assistantStatus: 'pending' | 'streaming' | 'done' | 'error' | 'timeout';
  assistantText: string;
  attachments?: ConvexPromptAttachment[] | null;
  createdAt: number;
  id: string;
  isGreeting?: boolean;
  prompt: string;
  streamId: string;
  threadId: string;
};

type ConvexPromptAttachment = {
  attachmentId?: string;
  contentType: string;
  fileName: string;
  fileSize: number;
  storageId: string;
};

type QueryHookState<Data> = {
  data: Data | undefined;
  error: { data: string } | undefined;
  isFetching: boolean;
  isLoading: boolean;
};

type CreateAiAssistantMessageMutation = (
  request: CreateAiAssistantMessageRequest
) => Promise<CreateAiAssistantMessageResult>;

type CachedConvexAuthToken = {
  expiresAt: number;
  patientAccessToken: string;
  token: string;
};

type PatientConvexAuthResponse = {
  expiresAt: number;
  token: string;
};

let convexReactClient: ConvexReactClient | null = null;
let cachedConvexAuthToken: CachedConvexAuthToken | null = null;
let inFlightConvexAuthTokenPromise: Promise<CachedConvexAuthToken | null> | null = null;
let inFlightConvexAuthTokenPatientAccessToken: string | null = null;
const cachedAttachmentBlobs = new Map<string, Blob>();
const inFlightAttachmentBlobRequests = new Map<string, Promise<Blob>>();

const asThreadId = (value: string) => value as Id<'threads'>;
const asThreadUploadId = (value: string) => value as Id<'threadUploads'>;
const asStorageId = (value: string) => value as Id<'_storage'>;

const createResponse = <Data>(data: Data) => ({
  data,
  errorCode: 0 as const,
  info: null,
  message: 'ok'
});

const resolveConvexSiteUrl = () =>
  import.meta.env.VITE_CONVEX_SITE_URL ?? import.meta.env.VITE_CONVEX_URL;

const resolveConvexUrl = () =>
  import.meta.env.VITE_CONVEX_URL ?? import.meta.env.VITE_CONVEX_SITE_URL;

const getRequiredConvexSiteUrl = () => {
  const convexSiteUrl = resolveConvexSiteUrl();

  if (!convexSiteUrl) {
    throw new Error('Missing VITE_CONVEX_SITE_URL or VITE_CONVEX_URL');
  }

  return convexSiteUrl;
};

const getRequiredConvexUrl = () => {
  const convexUrl = resolveConvexUrl();

  if (!convexUrl) {
    throw new Error('Missing VITE_CONVEX_URL or VITE_CONVEX_SITE_URL');
  }

  return convexUrl;
};

const getConvexReactClient = () => {
  if (!convexReactClient) {
    convexReactClient = new ConvexReactClient(getRequiredConvexUrl());
  }

  return convexReactClient;
};

const buildAiAssistantUrl = (path: string) => new URL(path, getRequiredConvexSiteUrl()).toString();
const getAttachmentBlobCacheKey = (attachmentId: string, accessToken: string) =>
  `${accessToken}:${attachmentId}`;

const readErrorMessage = async (response: Response) => {
  const body = await response
    .clone()
    .json()
    .catch(() => null);

  if (body && typeof body === 'object') {
    if ('error' in body && typeof body.error === 'string') {
      return body.error;
    }

    if ('details' in body && typeof body.details === 'string') {
      return body.details;
    }
  }

  const text = await response.text().catch(() => '');
  return text || 'Unable to initialize AI chat.';
};

const useAiAssistantAccessToken = () => {
  const { accessToken } = useAppSelector(selectUser);
  return (accessToken ?? '').trim();
};

const exchangePatientTokenForConvexToken = async (
  accessToken: string
): Promise<PatientConvexAuthResponse | null> => {
  const response = await fetch(buildAiAssistantUrl('/auth/convex-token'), {
    headers: {
      Authorization: accessToken
    },
    method: 'POST'
  });

  if (!response.ok) {
    return null;
  }

  const body = (await response.json().catch(() => null)) as PatientConvexAuthResponse | null;

  if (!body?.token || !Number.isFinite(body.expiresAt)) {
    return null;
  }

  return body;
};

const getCachedConvexAuthToken = (patientAccessToken: string) => {
  if (
    cachedConvexAuthToken &&
    cachedConvexAuthToken.patientAccessToken === patientAccessToken &&
    cachedConvexAuthToken.expiresAt - CONVEX_AUTH_REFRESH_LEEWAY_MS > Date.now()
  ) {
    return cachedConvexAuthToken;
  }

  return null;
};

const fetchPatientPortalConvexAccessToken = async (
  patientAccessToken: string,
  {
    allowNetworkFetch = true,
    forceRefreshToken = false
  }: { allowNetworkFetch?: boolean; forceRefreshToken?: boolean } = {}
) => {
  if (!patientAccessToken) {
    return null;
  }

  if (!forceRefreshToken) {
    const cachedToken = getCachedConvexAuthToken(patientAccessToken);

    if (cachedToken) {
      return cachedToken.token;
    }
  }

  if (
    inFlightConvexAuthTokenPromise &&
    inFlightConvexAuthTokenPatientAccessToken === patientAccessToken
  ) {
    const inFlightToken = await inFlightConvexAuthTokenPromise;
    return inFlightToken?.token ?? null;
  }

  if (!allowNetworkFetch) {
    return null;
  }

  const nextTokenPromise = (async () => {
    try {
      const nextToken = await exchangePatientTokenForConvexToken(patientAccessToken);

      if (!nextToken) {
        cachedConvexAuthToken = null;
        return null;
      }

      cachedConvexAuthToken = {
        expiresAt: nextToken.expiresAt,
        patientAccessToken,
        token: nextToken.token
      };

      return cachedConvexAuthToken;
    } catch {
      cachedConvexAuthToken = null;
      return null;
    }
  })();

  inFlightConvexAuthTokenPromise = nextTokenPromise;
  inFlightConvexAuthTokenPatientAccessToken = patientAccessToken;

  try {
    const nextToken = await nextTokenPromise;
    return nextToken?.token ?? null;
  } finally {
    if (inFlightConvexAuthTokenPromise === nextTokenPromise) {
      inFlightConvexAuthTokenPromise = null;
      inFlightConvexAuthTokenPatientAccessToken = null;
    }
  }
};

const usePatientPortalConvexAuth = () => {
  const accessToken = useAiAssistantAccessToken();

  const fetchAccessToken = useCallback(
    async ({ forceRefreshToken }: { forceRefreshToken: boolean }) => {
      return await fetchPatientPortalConvexAccessToken(accessToken, {
        allowNetworkFetch: forceRefreshToken,
        forceRefreshToken
      });
    },
    [accessToken]
  );

  return useMemo(
    () => ({
      fetchAccessToken,
      isAuthenticated: !!accessToken,
      isLoading: false
    }),
    [accessToken, fetchAccessToken]
  );
};

const mapAssistantStatus = (
  status: ConvexPromptRecord['assistantStatus']
): AiAssistantMessageStatus => (status === 'timeout' ? 'error' : status);

const mapThreadStatus = (
  status: ConvexThreadSummary['status'] | ConvexThreadAccess['status']
): AiAssistantMessageStatus | null => {
  if (status === 'in_progress') {
    return 'pending';
  }

  return null;
};

const mapPromptAttachment = (attachment: ConvexPromptAttachment): AiAssistantImageAttachment => ({
  attachmentId: attachment.attachmentId ?? attachment.storageId,
  contentType: attachment.contentType,
  fileName: attachment.fileName,
  fileSize: attachment.fileSize,
  storageId: attachment.storageId
});

const mapThreadSummary = (thread: ConvexThreadSummary): AiAssistantThreadSummary => ({
  conversationTitle: thread.conversationTitle || DEFAULT_AI_THREAD_TITLE,
  createdAt: thread.createdAt,
  id: thread.id,
  latestAssistantStatus: mapThreadStatus(thread.status),
  latestPreview: thread.latestPreview,
  updatedAt: thread.updatedAt,
  workflowId: AI_ASSISTANT_WORKFLOW_ID
});

const mapPromptToMessages = (prompt: ConvexPromptRecord): AiAssistantMessage[] => {
  const isGreeting = prompt.isGreeting ?? false;
  const userMessageId = `user:${prompt.id}`;
  const assistantStatus = mapAssistantStatus(prompt.assistantStatus);

  const assistantMessage: AiAssistantMessage = {
    body: prompt.assistantText,
    createdAt: prompt.createdAt,
    errorMessage: assistantStatus === 'error' ? 'Unable to complete this response.' : null,
    id: `assistant:${prompt.streamId}`,
    isGreeting,
    parentMessageId: isGreeting ? null : userMessageId,
    role: 'assistant',
    status: assistantStatus,
    streamId: prompt.streamId,
    updatedAt: prompt.createdAt,
    workflowId: AI_ASSISTANT_WORKFLOW_ID
  };

  // Greeting turns are agent-only: the patient prompt is empty, so emit no user bubble.
  if (isGreeting) {
    return [assistantMessage];
  }

  return [
    {
      attachments: prompt.attachments?.map(mapPromptAttachment) ?? [],
      body: prompt.prompt,
      createdAt: prompt.createdAt,
      errorMessage: null,
      id: userMessageId,
      parentMessageId: null,
      role: 'user',
      status: 'done',
      streamId: prompt.streamId,
      updatedAt: prompt.createdAt,
      workflowId: AI_ASSISTANT_WORKFLOW_ID
    },
    assistantMessage
  ];
};

const mapThreadDetail = (
  access: ConvexThreadAccess,
  prompts: ConvexPromptRecord[]
): AiAssistantThreadDetail => ({
  conversationTitle: access.conversationTitle || DEFAULT_AI_THREAD_TITLE,
  createdAt: access.createdAt ?? 0,
  id: access.threadId,
  latestAssistantStatus: mapThreadStatus(access.status),
  latestPreview: '',
  messages: prompts.flatMap(mapPromptToMessages),
  updatedAt: access.updatedAt ?? access.createdAt ?? 0,
  workflowId: AI_ASSISTANT_WORKFLOW_ID
});

export const streamAiAssistantMessage = async ({
  accessToken,
  onChunk,
  streamId
}: {
  accessToken: string;
  onChunk: (chunk: string) => void;
  streamId: string;
}) => {
  const convexAccessToken = await fetchPatientPortalConvexAccessToken(accessToken);

  if (!convexAccessToken) {
    throw new Error('Unable to authenticate AI chat stream.');
  }

  const response = await fetch(buildAiAssistantUrl('/chat-stream'), {
    body: JSON.stringify({
      kind: AI_ASSISTANT_THREAD_KIND,
      streamId
    }),
    headers: {
      Authorization: `Bearer ${convexAccessToken}`,
      'Content-Type': 'application/json'
    },
    method: 'POST'
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }

  if (!response.body) {
    throw new Error('AI chat stream returned no body.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      if (value) {
        onChunk(decoder.decode(value));
      }

      return;
    }

    onChunk(decoder.decode(value, { stream: true }));
  }
};

export const fetchAiAssistantAttachmentBlob = async (attachmentId: string, accessToken: string) => {
  const cacheKey = getAttachmentBlobCacheKey(attachmentId, accessToken);
  const cachedBlob = cachedAttachmentBlobs.get(cacheKey);

  if (cachedBlob) {
    return cachedBlob;
  }

  const inFlightRequest = inFlightAttachmentBlobRequests.get(cacheKey);

  if (inFlightRequest) {
    return await inFlightRequest;
  }

  const nextRequest = (async () => {
    const convexAccessToken = await fetchPatientPortalConvexAccessToken(accessToken);
    const response = await fetch(
      `${buildAiAssistantUrl('/thread-uploads/image')}?attachmentId=${encodeURIComponent(attachmentId)}`,
      {
        headers: {
          Authorization: convexAccessToken ? `Bearer ${convexAccessToken}` : accessToken
        },
        method: 'GET'
      }
    );

    if (!response.ok) {
      throw new Error(await readErrorMessage(response));
    }

    const blob = await response.blob();
    cachedAttachmentBlobs.set(cacheKey, blob);
    return blob;
  })();

  inFlightAttachmentBlobRequests.set(cacheKey, nextRequest);

  try {
    return await nextRequest;
  } finally {
    if (inFlightAttachmentBlobRequests.get(cacheKey) === nextRequest) {
      inFlightAttachmentBlobRequests.delete(cacheKey);
    }
  }
};

export const fetchAiAssistantAttachmentBlobUrl = async (
  attachmentId: string,
  accessToken: string
) => {
  const blob = await fetchAiAssistantAttachmentBlob(attachmentId, accessToken);
  return URL.createObjectURL(blob);
};

export const PatientPortalConvexProvider = ({ children }: { children: ReactNode }) =>
  createElement(
    ConvexProviderWithAuth,
    {
      client: getConvexReactClient(),
      useAuth: usePatientPortalConvexAuth
    },
    children
  );

export const useGetAiAssistantThreadsQuery = (
  arg?: void | typeof skipToken
): QueryHookState<GetAiAssistantThreadsResponse> => {
  const accessToken = useAiAssistantAccessToken();
  const shouldSkip = arg === skipToken || !accessToken;
  const threads = useQuery(api.threadAccess.listThreads, shouldSkip ? 'skip' : {}) as
    ConvexThreadSummary[] | undefined;
  const isLoading = !shouldSkip && threads === undefined;

  return {
    data: threads ? createResponse(threads.map(mapThreadSummary)) : undefined,
    error: undefined,
    isFetching: isLoading,
    isLoading
  };
};

export const useGetAiAssistantThreadQuery = (
  threadId: string | typeof skipToken
): QueryHookState<GetAiAssistantThreadResponse> => {
  const accessToken = useAiAssistantAccessToken();
  const shouldSkip = threadId === skipToken || !accessToken;
  const threadArgs = shouldSkip
    ? 'skip'
    : {
        threadId: asThreadId(threadId as string)
      };
  const access = useQuery(api.threadAccess.getThreadAccess, threadArgs) as
    ConvexThreadAccess | null | undefined;
  const prompts = useQuery(api.threadMessages.listChatMessages, threadArgs) as
    ConvexPromptRecord[] | undefined;
  const isLoading = !shouldSkip && (access === undefined || prompts === undefined);
  const isMissing = access === null || access?.createdAt === null;

  return {
    data:
      !isLoading && access && access.createdAt !== null && prompts
        ? createResponse(mapThreadDetail(access, prompts))
        : undefined,
    error: isMissing ? { data: 'Conversation not found.' } : undefined,
    isFetching: isLoading,
    isLoading
  };
};

export const useCreateAiAssistantMessageMutation = () => {
  const accessToken = useAiAssistantAccessToken();
  const createChatPrompt = useMutation(api.threadMessages.createChatPrompt);
  const [isLoading, setIsLoading] = useState(false);

  const mutate: CreateAiAssistantMessageMutation = async ({ attachmentIds, prompt, threadId }) => {
    if (!accessToken) {
      throw new Error('Missing AI assistant access token.');
    }

    setIsLoading(true);

    try {
      const response = await createChatPrompt({
        attachmentIds: attachmentIds?.map(asThreadUploadId),
        prompt,
        threadId: threadId ? asThreadId(threadId) : undefined,
        workflowKind: AI_ASSISTANT_THREAD_KIND
      });

      if (response.workflowKind !== AI_ASSISTANT_THREAD_KIND) {
        throw new Error('AI chat workflow kind mismatch.');
      }

      return createResponse({
        streamId: response.streamId,
        threadId: response.threadId,
        workflowKind: AI_ASSISTANT_THREAD_KIND
      });
    } finally {
      setIsLoading(false);
    }
  };

  return [mutate, { isLoading }] as const;
};

export const useGenerateAiAssistantImageUploadUrlMutation = () => {
  const accessToken = useAiAssistantAccessToken();
  const generateUploadUrl = useMutation(api.threadMessages.generateThreadImageUploadUrl);
  const [isLoading, setIsLoading] = useState(false);

  const mutate = async () => {
    if (!accessToken) {
      throw new Error('Missing AI assistant access token.');
    }

    setIsLoading(true);

    try {
      return await generateUploadUrl({});
    } finally {
      setIsLoading(false);
    }
  };

  return [mutate, { isLoading }] as const;
};

export const useRegisterAiAssistantImageUploadMutation = () => {
  const accessToken = useAiAssistantAccessToken();
  const registerUpload = useMutation(api.threadMessages.registerThreadImageUpload);
  const [isLoading, setIsLoading] = useState(false);

  const mutate = async ({ fileName, storageId }: { fileName: string; storageId: string }) => {
    if (!accessToken) {
      throw new Error('Missing AI assistant access token.');
    }

    setIsLoading(true);

    try {
      return await registerUpload({ fileName, storageId: asStorageId(storageId) });
    } finally {
      setIsLoading(false);
    }
  };

  return [mutate, { isLoading }] as const;
};

type StartAiChatRequest = {
  firstName?: string;
  idempotencyKey: string;
  reason: CallTopic;
  reasonDetail?: string;
  sourceContext?: { route: string };
};

type StartAiChatResult = {
  streamId: string;
  threadId: string;
  workflowKind: string;
};

export const useStartAiChatMutation = () => {
  const startAiChat = useMutation(api.threadMessages.startAiChat);
  const [isLoading, setIsLoading] = useState(false);

  const mutate = async (request: StartAiChatRequest): Promise<StartAiChatResult> => {
    setIsLoading(true);

    try {
      return await startAiChat(request);
    } finally {
      setIsLoading(false);
    }
  };

  return [mutate, { isLoading }] as const;
};
